import * as Sentry from '@sentry/react';
import geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';
import {Navigate, useNavigate} from 'react-router-dom';

import {usePushNotification} from 'app/components/Notification';
import {api} from 'app/modules/Remote';
import {ApiLensConvertFeatureFile} from 'app/modules/Remote/Admin';
import {HoverProperty, HydratedFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {
  ApiOrganization,
  ApiOrganizationAreaUnit,
  getAreaUnit,
} from 'app/modules/Remote/Organization';
import {useGeoJsonFeaturesLoader} from 'app/providers/FeaturesProvider';
import {useProjects} from 'app/providers/ProjectsProvider';
import {recordEvent} from 'app/tools/Analytics';
import {ApiResponseError} from 'app/utils/apiUtils';
import * as C from 'app/utils/constants';
import {findPrimaryFeatureCollection} from 'app/utils/featureCollectionUtils';
import * as featureCollectionUtils from 'app/utils/featureCollectionUtils';
import {useStateWithDeps} from 'app/utils/hookUtils';
import {createLayerKeysFromLayerObject, createLayerObjectFromLayerKeys} from 'app/utils/layerUtils';
import {getOrgPrefix, useDefaultLayersBySource} from 'app/utils/organizationUtils';
import {splitOversizedFeature, stripBackendValidationProps} from 'app/utils/polygonSplit';
import * as projectUtils from 'app/utils/projectUtils';
import * as routeUtils from 'app/utils/routeUtils';

import {buildUploadEdits} from './uploadEdits';

import {ManagePropertiesMode, isDrawnPropertiesMode} from '.';

export type FeatureId = number;

export interface NumberedFeatureCollection extends geojson.FeatureCollection {
  features: NumberedFeature[];
}
export interface NumberedFeature extends geojson.Feature {
  id: FeatureId;
}

export type GroupPropertiesOptions = 'individual' | 'group' | null;

export interface ManageFeaturesState {
  filename: string | null;
  uploadedFeatureCollection: NumberedFeatureCollection | null;
  isConvertingFile: boolean;
  nameField: string | null;
  nameValues: I.Map<FeatureId, string> | null;
  partNameValues: I.Map<FeatureId, string | null> | null;
  partNameField: string | null;
  selectedFeature: NumberedFeature | null;
  // Per-property-name combine decision (default 'group'); read via .get(name).
  groupModeByName: I.Map<string, 'group' | 'individual'>;
  problems: string[] | null;
  featureCollectionErrors: string[] | null;
  isUploading: boolean;
  selectedFeatureIds: I.Set<FeatureId>;
  areaUnit: ApiOrganizationAreaUnit;
  allUploadableFeatureIds: I.Set<FeatureId>;
  portfolioName: string | null;
  existingFeatureCollection?: HydratedFeatureCollection | null;
  warningsMap: Record<string, string[] | null>;
  selectedLayerKeys: string[];
  selectedOverlayIds: number[];
  featureCollectionWarnings?: Set<string>;
  originalGeometries: geojson.Geometry[];
  hoverProperties: HoverProperty[];
  // Features the user has dismissed via the row × — hidden from the table,
  // excluded from the portfolio total / map outline, and deselected so they
  // don't upload. The FC itself is left intact (file-based submit relies on
  // stable feature indices), so removal is tracked as a separate id set.
  removedFeatureIds: I.Set<FeatureId>;
}

export interface ManageFeaturesActions {
  setFilename: React.Dispatch<React.SetStateAction<string | null>>;
  setUploadedFeatureCollection: React.Dispatch<
    React.SetStateAction<NumberedFeatureCollection | null>
  >;
  setFilePath: React.Dispatch<React.SetStateAction<string | null>>;
  setIsConvertingFile: React.Dispatch<React.SetStateAction<boolean>>;
  setNameField: React.Dispatch<React.SetStateAction<string | null>>;
  setNameValues: React.Dispatch<React.SetStateAction<I.Map<FeatureId, string> | null>>;
  setPartNameValues: React.Dispatch<React.SetStateAction<I.Map<FeatureId, string | null> | null>>;
  setPartNameField: React.Dispatch<React.SetStateAction<string | null>>;
  setSelectedFeature: React.Dispatch<React.SetStateAction<NumberedFeature | null>>;
  convertFile: (file: File, mode?: ManagePropertiesMode) => Promise<string | undefined>;
  uploadFeatures: () => Promise<void>;
  uploadDrawnFeatures: () => Promise<void>;
  splitFeature: (featureId: FeatureId) => Promise<void>;
  removeFeature: (featureId: FeatureId) => void;
  setGroupModeForName: (name: string, value: 'group' | 'individual') => void;
  setSelectedFeatureIds: React.Dispatch<React.SetStateAction<I.Set<FeatureId>>>;
  setProblems: React.Dispatch<React.SetStateAction<string[] | null>>;
  setFeatureCollectionErrors: React.Dispatch<React.SetStateAction<string[] | null>>;
  setPortfolioName: React.Dispatch<React.SetStateAction<string | null>>;
  setSelectedLayerKeys: React.Dispatch<React.SetStateAction<string[]>>;
  setSelectedOverlayIds: React.Dispatch<React.SetStateAction<number[]>>;
  setHoverProperties: React.Dispatch<React.SetStateAction<HoverProperty[]>>;
}

interface ManageFeaturesContextValue {
  manageFeaturesState: ManageFeaturesState;
  manageFeaturesActions: ManageFeaturesActions;
  loading: boolean;
}

export const ManageFeaturesContext = React.createContext<ManageFeaturesContextValue | undefined>(
  undefined
);

const ManageFeaturesProvider: React.FunctionComponent<
  React.PropsWithChildren<{
    organization: I.MapAsRecord<I.ImmutableFields<ApiOrganization>>;
    projectId?: string;
    mode: ManagePropertiesMode;
  }>
> = ({organization, projectId, mode, children}) => {
  const navigate = useNavigate();
  // useProjects uses meta.loading for its loading state
  const [projects, featureCollections, projectsActions, meta] = useProjects();

  // useGeoJsonFeatures doesn't distinguish a loading state and will just return an empty list
  // until data is returned
  const getFeatures = useGeoJsonFeaturesLoader();

  // projectId param is a prefix of the full UUID
  const project = React.useMemo(() => {
    if (!projectId || !projects) {
      return null;
    }

    return projectUtils.findProjectInProjects(projects, projectId);
  }, [projectId, projects]);

  const existingFeatureCollection = React.useMemo(() => {
    if (!project) {
      return null;
    }

    const unhydratedFeatureCollection =
      project && findPrimaryFeatureCollection(project, featureCollections);

    return unhydratedFeatureCollection
      ? // if a featureCollection exists, return it as a HydratedFeatureCollection
        featureCollectionUtils.hydratedFeatureCollection(
          unhydratedFeatureCollection,
          getFeatures(unhydratedFeatureCollection)
        )
      : null;
  }, [project, featureCollections, getFeatures]);

  // In file upload mode, this featureCollection is only updated when a new file is uploaded.
  // In draw mode, it is updated whenever a new feature is drawn or edited.
  // TODO (maya): rename this variable something that makes more sense now!
  const [uploadedFeatureCollection, setUploadedFeatureCollection] =
    React.useState<NumberedFeatureCollection | null>(null);
  const [filename, setFilename] = React.useState<string | null>(null);
  const [isConvertingFile, setIsConvertingFile] = React.useState(false);
  const [blockingErrors, setBlockingErrors] = React.useState<string[] | null>(null);
  // Per-property-name combine decision. Same-named features default to being
  // combined into one multi-location property ('group'); 'individual' keeps
  // them as separate properties (de-duped "Name - PartName" at submit). Keyed
  // by the property name so the choice is contextual to each grouped row.
  const [groupModeByName, setGroupModeByName] = React.useState<I.Map<string, 'group' | 'individual'>>(
    I.Map()
  );
  const setGroupModeForName = React.useCallback(
    (name: string, value: 'group' | 'individual') =>
      setGroupModeByName((prev) => prev.set(name, value)),
    []
  );
  const [selectedFeature, setSelectedFeature] = React.useState<NumberedFeature | null>(null);
  const [filePath, setFilePath] = React.useState<string | null>(null);
  const [isUploading, setIsUploading] = React.useState(false);
  const [featureCollectionErrors, setFeatureCollectionErrors] = React.useState<string[] | null>(
    null
  );
  const [portfolioName, setPortfolioName] = React.useState<string | null>(null);
  const [partNameValues, setPartNameValues] = React.useState<I.Map<
    FeatureId,
    string | null
  > | null>(null);

  // Defaults pulled from org defaults plus any premium sources the org subscribes to;
  // undefined while the premium-source fetch is in flight, in which case we fall
  // back to org defaults so the form is usable immediately.
  const defaultLayersBySource = useDefaultLayersBySource(organization);

  //TODO(eva): it would be good to maintain selectedLayerKeys/selectedOverlayIds as one list.
  //once we have the notion of a shared libraryKey this would be good to tackle.
  const [selectedLayerKeys, setSelectedLayerKeys] = useStateWithDeps<string[]>(
    createLayerKeysFromLayerObject(
      defaultLayersBySource ??
        (organization.get('defaultLayersBySource').toJS() as Record<string, string[]>)
    ),
    [existingFeatureCollection, defaultLayersBySource]
  );
  const [selectedOverlayIds, setSelectedOverlayIds] = React.useState<number[]>([]);
  const [hoverProperties, setHoverProperties] = React.useState<HoverProperty[]>([]);
  // Geometries we've replaced — rendered on the map as a thick white outline.
  const [originalGeometries, setOriginalGeometries] = React.useState<geojson.Geometry[]>([]);
  // Features dismissed via the row × (see ManageFeaturesState.removedFeatureIds).
  const [removedFeatureIds, setRemovedFeatureIds] = React.useState<I.Set<FeatureId>>(I.Set());
  React.useEffect(() => {
    if (uploadedFeatureCollection === null) {
      setOriginalGeometries([]);
      setRemovedFeatureIds(I.Set());
    }
  }, [uploadedFeatureCollection]);

  const removeFeature = (featureId: FeatureId) =>
    setRemovedFeatureIds((prev) => prev.add(featureId));

  // If in drawMode, set defaults on the names here to be able to share name based grouping logic
  // When uploading an overlay (in overlay mode), use the first selected hover property as the feature display name
  const [nameField, setNameField] = useStateWithDeps<string | null>(
    isDrawnPropertiesMode(mode)
      ? 'name'
      : mode === 'overlay' || mode === 'updateOverlay'
        ? (hoverProperties[0]?.key ?? null)
        : null,
    [mode, hoverProperties]
  );
  const [partNameField, setPartNameField] = useStateWithDeps<string | null>(
    isDrawnPropertiesMode(mode) ? 'partName' : null,
    [mode]
  );
  const [nameValues, setNameValues] = useStateWithDeps(
    // in overlays mode, if we don't have a defined nameField, we want to pass a list of empty
    // strings back as our feature nameValues. features with names that are empty strings will
    // not have a name displayed on hover.
    // in all other modes, a nameField must be selected in order to set nameValues.
    uploadedFeatureCollection && (nameField || mode === 'overlay' || mode === 'updateOverlay')
      ? I.Map<FeatureId, string>(
          uploadedFeatureCollection.features.map((feature) => [
            feature.id,
            nameField ? feature.properties![nameField] || '' : '',
          ])
        )
      : null,
    [nameField, uploadedFeatureCollection]
  );
  // All features that are valid candidates for upload (no backend problems and
  // a non-empty name for the chosen field). Does NOT exclude removedFeatureIds
  // so the auto-select effect below doesn't refire — and wipe the user's
  // manual deselections — every time they click × on a row.
  const baseUploadableFeatureIds = React.useMemo(() => {
    return I.Set<FeatureId>(
      uploadedFeatureCollection?.features.reduce((selected: number[], f) => {
        // If there is no nameField selected yet, we default to true to allow
        // all features to appear on the map initally. After a nameFiled is selected, then
        // it will be used to filter out blank name values
        if (
          f.properties &&
          !f.properties['__UPSTREAM_PROBLEMS'] &&
          (nameField ? f.properties[nameField] !== null : true)
        ) {
          selected.push(f.id);
        }
        return selected;
      }, []) ?? []
    );
  }, [uploadedFeatureCollection, nameField]);
  // The set actually surfaced to consumers (header "select all", etc.). Excludes
  // rows the user has dismissed via × so the checkbox state stays consistent
  // even though the auto-select effect doesn't watch removedFeatureIds.
  const allUploadableFeatureIds = React.useMemo(
    () => baseUploadableFeatureIds.subtract(removedFeatureIds),
    [baseUploadableFeatureIds, removedFeatureIds]
  );
  // Initially select all valid uploadable featureIds
  const [selectedFeatureIds, setSelectedFeatureIds] = React.useState<I.Set<number>>(I.Set([]));
  // Ref so auto-select can subtract removedFeatureIds without depending on it (avoids wiping × deselections).
  const removedFeatureIdsRef = React.useRef(removedFeatureIds);
  removedFeatureIdsRef.current = removedFeatureIds;
  React.useEffect(() => {
    setSelectedFeatureIds(baseUploadableFeatureIds.subtract(removedFeatureIdsRef.current));
  }, [baseUploadableFeatureIds, setSelectedFeatureIds]);

  // Filter out any features with empty nameValues from the selectedFeatureIds list, unless we
  // are in overlays mode (it's ok to upload features with empty names in overlays mode)
  React.useEffect(() => {
    if (nameValues && mode !== 'overlay' && mode !== 'updateOverlay') {
      setSelectedFeatureIds((selectedFeatureIdsState) =>
        selectedFeatureIdsState.filter((fId) => !!nameValues.get(fId!)).toSet()
      );
    }
  }, [nameValues, setSelectedFeatureIds, mode]);

  const areaUnit = getAreaUnit(organization);

  React.useEffect(() => {
    if (project) {
      setPortfolioName(project.get('name'));
    }
  }, [project]);

  // TODO (Maya): This needs to be expanded upon.
  // Will include geometry warnings potentially as well. But there are assumptions made currently that it is
  // only for name collisions. If it is modified, those checks will need to be changed as well
  // Right now this only is checking for names of new features matching the new features
  // matching the names of pre-existing features.
  // Compute warnings over every feature with a name — independent of
  // selection. A name conflict is a fact about the data, not about what the
  // user happens to have checked, so we don't want unchecking a row to make
  // its warning disappear (and flip the row out of Warnings into Ready).
  // Reads `nameValues` — only changes on commit (NameInput's onBlur/Enter), so the section
  // doesn't churn on every keystroke.
  const warningsMap: Record<string, string[] | null> = React.useMemo(() => {
    if (!uploadedFeatureCollection || !existingFeatureCollection || !nameValues) {
      return {};
    }
    const existingPropertyNames: I.Set<string> = I.Set(
      existingFeatureCollection.get('features').map((f) => f?.getIn(['properties', 'name']))
    );
    return uploadedFeatureCollection.features.reduce<Record<string, string[] | null>>(
      (acc, feature) => {
        const editedName = nameValues.get(feature.id);
        acc[String(feature.id)] =
          editedName && existingPropertyNames.get(editedName)
            ? ['Property name matches a feature already existing in your portfolio']
            : null;
        return acc;
      },
      {}
    );
  }, [uploadedFeatureCollection, existingFeatureCollection, nameValues]);

  // Clear partNameValues whenever the inputs that drive a fresh seed change —
  // a new file (or the splitFeature roundtrip) or the user picking a different
  // file attribute as the part-name source. The seeding effect below then
  // re-populates from scratch.
  React.useEffect(() => {
    setPartNameValues(null);
  }, [uploadedFeatureCollection, partNameField]);

  // Populate each location's part name for multi-location properties (a name
  // shared by more than one feature, or a feature with a name-conflict
  // warning). Count occurrences directly off the FC rather than gating on
  // selectedFeatureIds — selection updates lag by a render after the FC
  // changes, which used to make Split seed empty strings. The guard below
  // (preserve when prev !== null) keeps name-edit keystrokes from wiping the
  // user's part-name inputs.
  React.useEffect(() => {
    if (!nameValues || !partNameField || !uploadedFeatureCollection) {
      setPartNameValues(null);
      return;
    }
    setPartNameValues((prev) => {
      if (prev !== null) return prev;
      const nameCount = new Map<string, number>();
      uploadedFeatureCollection.features.forEach((f) => {
        const n = nameValues.get(f.id) ?? '';
        nameCount.set(n, (nameCount.get(n) ?? 0) + 1);
      });
      return I.Map<FeatureId, string | null>(
        uploadedFeatureCollection.features.map((feature) => [
          feature.id,
          (nameCount.get(nameValues.get(feature.id) ?? '') ?? 0) > 1 || warningsMap[feature.id]
            ? feature.properties![partNameField]
            : '',
        ])
      );
    });
  }, [uploadedFeatureCollection, partNameField, nameValues, warningsMap]);

  const {featureCollectionWarnings, loading: fcWarningsLoading} = useFeatureCollectionWarnings(
    uploadedFeatureCollection,
    selectedFeatureIds,
    mode
  );

  React.useEffect(() => {
    if (fcWarningsLoading) {
      setIsUploading(true);
    } else {
      setIsUploading(false);
    }
  }, [fcWarningsLoading]);

  const PROCESSING_ERROR_TEXT = 'Processing error, please try again.';

  const convertFile = async (file: File, mode?: ManagePropertiesMode) => {
    try {
      setBlockingErrors(null);
      setIsConvertingFile(true);

      const {problems, featureCollection, gcs_shapefile_path}: ApiLensConvertFeatureFile = (
        mode === 'overlay' || mode === 'updateOverlay'
          ? await api.lens.uploader.convertOverlay(file)
          : await api.lens.uploader.convertFile(file)
      ).toJS() as ApiLensConvertFeatureFile;

      if (problems?.length) {
        setBlockingErrors(problems);
        return;
      }

      // If no features are present in the featureCollection, flag as error to the user and prevent
      // from proceeding onwards.
      if (!featureCollection.features.length) {
        setBlockingErrors(['No features were found in this file.']);
        return;
      }

      // Endpoint returns features with ids starting at 0. Add 1 to all ids to eliminate risk of a 0 id.
      const safeIdFeatureCollection: NumberedFeatureCollection = {
        ...featureCollection,
        features: featureCollection.features.map((f) => {
          // Need to cast this id to a number since the featureCollection json response we are getting from
          // the api encodes this number as a string. Then we can safely treat this ID as a number from
          // this point onwards
          const feature = {...f, id: Number(f.id) + 1};
          return feature;
        }),
      };

      setUploadedFeatureCollection(safeIdFeatureCollection);
      setFilePath(gcs_shapefile_path ?? null);

      // Return filepath for use in draw properties workflow. Cannot rely on setState to set
      // filepath in time when uploadFeatures is called immediately afterwards.
      return gcs_shapefile_path;
    } catch (e) {
      const apiError = e as ApiResponseError;
      setBlockingErrors(
        //errors caught here could either be intentionally handled problems, or unexpected exceptions
        //hence this type vagueness on what error is coming back from the API
        apiError.body?.problems || [apiError.error.message || PROCESSING_ERROR_TEXT]
      );
      // We are displaying these problems to the user and not letting them proceed onwards. Not logging
      // these errors in Sentry because it may just become noise and displaying an error here
      // is things working as intended.
    } finally {
      setIsConvertingFile(false);
    }
  };

  const uploadFeatures = async (drawnFilePath?: string) => {
    try {
      setIsUploading(true);

      const gcsFilePath = drawnFilePath || filePath;
      // a user does not need nameValues in overlay mode
      if (gcsFilePath && (nameValues || mode === 'overlay' || mode === 'updateOverlay')) {
        let featureCollectionId: number | undefined = existingFeatureCollection
          ? existingFeatureCollection.get('id')
          : undefined;

        const {nameEdits, partNameEdits, featureIdsToExclude} = buildUploadEdits({
          uploadedFeatureCollection,
          nameValues,
          partNameValues,
          selectedFeatureIds,
          groupModeByName,
          warningsMap,
        });

        if (mode === 'updateOverlay') {
          // The :projectId URL param is reused for several modes; in updateOverlay
          // it actually carries the feature collection id of the overlay being updated.
          const overlayFeatureCollectionId = parseInt(projectId!);
          if (isNaN(overlayFeatureCollectionId)) {
            setFeatureCollectionErrors(['Invalid overlay ID.']);
            return;
          }
          await api.featureCollections.updateOverlay(
            overlayFeatureCollectionId,
            gcsFilePath,
            nameEdits,
            featureIdsToExclude,
            hoverProperties
          );

          recordEvent('Updated overlay from file', {
            overlayFeatureCollectionId,
            featureCount: selectedFeatureIds.size,
          });

          pushNotification({
            message: 'Your overlay is being updated. This may take a few minutes.',
            autoHideDuration: 10000,
          });
          navigate(`/${getOrgPrefix(organization)}/settings/overlays`);

          // if we are in overlay mode, create the featureCollection without creating a project
        } else if (mode === 'overlay') {
          // Kick off the overlay upload
          await api.featureCollections.uploadOverlay(
            portfolioName!, //portfolioName will be the overlay's name
            gcsFilePath,
            nameEdits,
            featureIdsToExclude,
            hoverProperties
          );

          recordEvent('Uploaded overlay from file', {
            name: portfolioName!,
            featureCount: selectedFeatureIds.size,
          });

          //return to overlays settings, where they will be able to do stuff with the overlay
          //TODO(eva) might be good to have a toast here saying that processing may take a few min
          navigate(`/${getOrgPrefix(organization)}/settings/overlays`);

          // if we are not in overlay mode, assume we should be creating a project and associating
          // a featureCollection to it
        } else {
          // If an existing fc wasn't specified, create the portfolio and featureCollection
          if (!featureCollectionId) {
            const result = await projectsActions.createProject(
              organization,
              portfolioName ? portfolioName : 'New Portfolio',
              createLayerObjectFromLayerKeys(selectedLayerKeys),
              selectedOverlayIds
            );
            featureCollectionId = result?.featureCollectionId;

            if (!featureCollectionId) {
              setFeatureCollectionErrors([PROCESSING_ERROR_TEXT]);
              Sentry.captureException(
                new Error('Error creating project and feature collection in uploader')
              );
              return;
            }
          }

          // Kick off the feature upload
          await api.featureCollections.uploadFeaturesFromFile(
            featureCollectionId,
            gcsFilePath,
            nameEdits,
            partNameEdits,
            featureIdsToExclude,
            mode ?? 'upload' // for now assume upload if we are not explicitly in draw mode
          );

          recordEvent(
            drawnFilePath ? 'Created properties from drawing' : 'Uploaded properties from file',
            {
              projectId: projectId ?? '',
              featureCount: selectedFeatureIds.size,
            }
          );

          // Clear cached featureCollection and refetch so correct processing
          // status is reflected immediately
          await projectsActions.refreshFeatureCollection(featureCollectionId);
          const orgIdPrefix = routeUtils.makeUuidPrefix(organization.get('id'));

          let pathToNavigateTo = `/${orgIdPrefix}/projects`;
          if (project) {
            pathToNavigateTo += `/${routeUtils.makeUuidPrefix(project.get('id'))}`;
            pushNotification({
              message: 'It may take a few moments for newly added properties to appear',
              autoHideDuration: 10000,
            });
          }

          navigate(pathToNavigateTo);
        }
      }
      setIsUploading(false);
    } catch (e) {
      setIsUploading(false);
      setFeatureCollectionErrors(
        //errors caught here could either be intentionally handled problems, or unexpected exceptions
        //hence this type vagueness on what error is coming back from the API
        (e as any)?.body?.problems || [(e as any)?.error?.message || PROCESSING_ERROR_TEXT]
      );
      console.error(e);
      Sentry.captureException(e, {
        extra: {
          filePath: filePath,
          nameField,
          partNameField,
        },
      });
    }
  };

  // Run an equal-area recursive bisection on a single oversized feature in
  // the uploaded FC, then re-run convertFile so the backend re-validates the
  // result and refreshes _UPSTREAM_ACRES / __UPSTREAM_PROBLEMS / filePath.
  // Pieces share the original name and get numbered locations (partName) so
  // the existing multi-location grouping logic picks them up as one property.
  const splitFeature = async (featureId: FeatureId) => {
    if (!uploadedFeatureCollection) return;
    const target = uploadedFeatureCollection.features.find((f) => f.id === featureId);
    if (!target) return;

    const pieces = splitOversizedFeature(target, C.MAX_AREA_ACRES);
    if (pieces.length <= 1) return;

    // In upload mode name edits live in nameValues, not in the FC, so prefer
    // the edited value over the original file property.
    const originalName =
      (nameValues?.get(featureId) ?? null) ||
      (nameField && (target.properties?.[nameField] as string | undefined)) ||
      'Property';
    const nameKey = nameField ?? 'name';
    const partNameKey = partNameField ?? 'partName';
    const namedPieces = pieces.map((piece, i) => ({
      ...piece,
      properties: {
        ...piece.properties,
        [nameKey]: originalName,
        name: originalName,
        [partNameKey]: `${i + 1}`,
      },
    }));

    // Carry over the other features, baking in the user's in-progress name /
    // location edits (held in nameValues / partNameValues, separate from the
    // FC) so the convertFile round-trip below doesn't reset every row to its
    // original file value. Also strip the backend-stamped fields — convertFile's
    // validator APPENDS to __UPSTREAM_PROBLEMS rather than replacing it, so
    // leaving them in would double every other row's problems on each split.
    const carriedOver = uploadedFeatureCollection.features
      .filter((f) => f.id !== featureId && !removedFeatureIds.has(f.id))
      .map((f) => {
        const properties = stripBackendValidationProps(f.properties) ?? {};
        const editedName = nameValues?.get(f.id);
        if (nameField && editedName != null) {
          properties[nameField] = editedName;
          properties.name = editedName;
        }
        const editedPartName = partNameValues?.get(f.id);
        if (partNameField && editedPartName != null) {
          properties[partNameField] = editedPartName;
        }
        return {...f, properties};
      });
    const replacedFC: geojson.FeatureCollection = {
      type: 'FeatureCollection',
      features: [...carriedOver, ...namedPieces],
    };

    const fileObject: File = new File([JSON.stringify(replacedFC)], 'split.geojson', {
      type: 'application/json',
    });

    recordEvent('Split oversized property', {
      featureId,
      pieceCount: namedPieces.length,
    });

    await convertFile(fileObject, mode);

    // Clear stale IDs — convertFile re-numbers; carry-over already dropped removed features.
    setRemovedFeatureIds(I.Set());

    // Remember the original outline so the map can show it as a ghost line
    // overlaying the split pieces.
    setOriginalGeometries((prev) => [...prev, target.geometry]);

    // In upload mode partNameField starts null; now that the split pieces
    // share a name and carry a partName property, surface the multi-location
    // grouping UI by selecting it. Draw and parcel modes already default to
    // 'partName', so this is a no-op there.
    if (!partNameField) {
      setPartNameField(partNameKey);
    }

    pushNotification({
      message: `Split "${originalName}" into ${namedPieces.length} sites.`,
      autoHideDuration: 6000,
    });
  };

  // Upload features in draw mode. Utilizes the pre-existing endpoints for file
  // uploads by writing the drawn features to a file, uploading that file, then
  // sending the relevant info to the uploadFeaturesFromFile endpoint
  const uploadDrawnFeatures = async () => {
    if (uploadedFeatureCollection) {
      const JSONstring = JSON.stringify(uploadedFeatureCollection);
      const fileObject: File = new File([JSONstring], 'drawnFeatures.geojson', {
        type: 'application/json',
      });

      try {
        const uploadedFilePath = await convertFile(fileObject);
        // convertFile reports why it failed via `problems`; without this guard
        // uploadFeatures no-ops on the missing path and the submit looks ignored.
        if (!uploadedFilePath) {
          return;
        }
        await uploadFeatures(uploadedFilePath);
      } catch (e) {
        console.error(e);
      }
    }
  };

  const manageFeaturesState: ManageFeaturesState = {
    filename,
    uploadedFeatureCollection,
    isConvertingFile,
    nameField,
    nameValues,
    partNameField,
    partNameValues,
    selectedFeature,
    groupModeByName,
    problems: blockingErrors,
    isUploading,
    selectedFeatureIds,
    areaUnit,
    featureCollectionErrors,
    allUploadableFeatureIds,
    existingFeatureCollection,
    portfolioName,
    warningsMap,
    selectedLayerKeys,
    selectedOverlayIds,
    featureCollectionWarnings,
    hoverProperties,
    originalGeometries,
    removedFeatureIds,
  };

  const manageFeaturesActions: ManageFeaturesActions = {
    setFilename,
    setUploadedFeatureCollection,
    setFilePath,
    setIsConvertingFile,
    setNameField,
    setNameValues,
    setPartNameField,
    setPartNameValues,
    setSelectedFeature,
    convertFile,
    uploadFeatures,
    uploadDrawnFeatures,
    splitFeature,
    removeFeature,
    setGroupModeForName,
    setSelectedFeatureIds,
    setProblems: setBlockingErrors,
    setFeatureCollectionErrors,
    setPortfolioName,
    setSelectedLayerKeys,
    setSelectedOverlayIds,
    setHoverProperties,
  };

  const value = React.useMemo(
    (): ManageFeaturesContextValue => ({
      manageFeaturesActions: manageFeaturesActions,
      manageFeaturesState: manageFeaturesState,
      loading: meta.loading,
    }),
    [manageFeaturesActions, manageFeaturesState, meta.loading]
  );

  // Return values: either a redirect if an existingFeatureCollection can't be found with a project,
  // or provide the context.
  const pushNotification = usePushNotification();
  if (projectId && mode !== 'updateOverlay' && !existingFeatureCollection && !meta.loading) {
    pushNotification({
      message:
        'There was a problem loading that Portfolio. Ensure you are signed in to the correct organization.',
      autoHideDuration: 4000,
    });

    // The only case I can think of for this is if they somehow clicked on an uploader link for an org they don't have access to
    const orgIdPrefix = routeUtils.makeUuidPrefix(organization.get('id'));
    return <Navigate to={`/${orgIdPrefix}/projects`} replace />;
  }

  return <ManageFeaturesContext.Provider value={value}>{children}</ManageFeaturesContext.Provider>;
};

export function useManageFeatures(): ManageFeaturesContextValue {
  const value = React.useContext(ManageFeaturesContext);

  if (!value) {
    throw new Error('useManageFeatures must be beneath a ManageFeaturesProvider');
  }

  return value;
}

export const FakeManageFeaturesProvider: React.FunctionComponent<
  React.PropsWithChildren<{
    manageFeaturesActions: ManageFeaturesActions;
    manageFeaturesState: ManageFeaturesState;
    loading: boolean;
  }>
> = ({children, manageFeaturesActions, manageFeaturesState, loading}) => {
  const value = React.useMemo(
    (): ManageFeaturesContextValue => ({
      manageFeaturesActions: manageFeaturesActions,
      manageFeaturesState: manageFeaturesState,
      loading: loading,
    }),
    [manageFeaturesActions, manageFeaturesState, loading]
  );

  return <ManageFeaturesContext.Provider value={value}>{children}</ManageFeaturesContext.Provider>;
};

// Some warnings, like for restricted areas, only exist in the
// response for a converted file. This function will query the
// convert API for the FC that we ultimately want to upload,
// and populate warnings.
async function getConvertedFC(featureCollection: geojson.FeatureCollection) {
  const JSONstring = JSON.stringify(featureCollection);
  const fileObject: File = new File([JSONstring], 'drawnFeatures.geojson', {
    type: 'application/json',
  });

  const {featureCollection: fcToVerify} = (
    await api.lens.uploader.convertFile(fileObject, 'verify')
  ).toJS() as ApiLensConvertFeatureFile;
  return fcToVerify;
}

// Go through the featureCollection and collect all the feature warnings
function parseFeatureCollectionWarnings(featureCollection: geojson.FeatureCollection): Set<string> {
  const featureCollectionWarningsSet = new Set<string>();
  featureCollection.features.forEach((feature) => {
    const featureWarnings = feature.properties?.['__UPSTREAM_WARNINGS'];
    if (featureWarnings) {
      featureWarnings.forEach((warning) => {
        featureCollectionWarningsSet.add(warning);
      });
    }
  });
  return featureCollectionWarningsSet;
}

const hashFCGeoms = (uploadedFeatureCollection: NumberedFeatureCollection | null) => {
  const hash = uploadedFeatureCollection?.features.map((f) => JSON.stringify(f.geometry)).join(',');
  return hash ?? null;
};

// Get warnings for the featureCollection as a whole
const useFeatureCollectionWarnings = (
  uploadedFeatureCollection: NumberedFeatureCollection | null,
  selectedFeatureIds: I.Set<FeatureId>,
  mode: ManagePropertiesMode
) => {
  const [featureCollectionWarnings, setFeatureCollectionWarnings] = React.useState<Set<string>>(
    new Set()
  );
  const [loading, setLoading] = React.useState(false);
  const [convertedFC, setConvertedFC] = React.useState<geojson.FeatureCollection | null>(null);
  const [featureCollectionGeometriesHash, setFeatureCollectionGeometriesHash] = React.useState<
    string | null
  >(null);

  React.useEffect(() => {
    if (mode === 'overlay' || mode === 'updateOverlay') return;

    // No need to call api if the geometries haven't changed
    if (hashFCGeoms(uploadedFeatureCollection) === featureCollectionGeometriesHash) {
      return;
    }
    if (hashFCGeoms(uploadedFeatureCollection)) {
      setFeatureCollectionGeometriesHash(hashFCGeoms(uploadedFeatureCollection));
    }
    const fetchData = async () => {
      if (uploadedFeatureCollection) {
        setLoading(true);
        try {
          const result = await getConvertedFC(uploadedFeatureCollection);
          if (result) setConvertedFC(result);
        } catch (e) {
          console.error('Unable to convert drawn features into an uploadable format', e);
        } finally {
          setLoading(false);
        }
      } else {
        setConvertedFC(uploadedFeatureCollection);
      }
    };
    fetchData();
  }, [featureCollectionGeometriesHash, uploadedFeatureCollection, mode]);

  // Get warnings for the featureCollection as a whole
  React.useEffect(() => {
    if (mode === 'overlay' || mode === 'updateOverlay') return;

    const getFileConversionWarnings = async () => {
      if (uploadedFeatureCollection && uploadedFeatureCollection.features.length > 0) {
        if (convertedFC) {
          // Only check the selection for warnings
          const filteredConvertedFC = convertedFC.features.filter(
            (f) => selectedFeatureIds.has(Number(f.id) + 1) // +1 because the selectedFeatureIds are 1-indexed
          );
          const warningSet = parseFeatureCollectionWarnings({
            ...convertedFC,
            features: filteredConvertedFC,
          });
          setFeatureCollectionWarnings(warningSet);
        }
      } else {
        setFeatureCollectionWarnings(new Set());
      }
    };
    getFileConversionWarnings();
  }, [convertedFC, selectedFeatureIds, uploadedFeatureCollection, mode]);

  return {
    featureCollectionWarnings,
    loading,
  };
};

export default ManageFeaturesProvider;
